import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { runInAction } from 'mobx'
import ProductScoutStore from './ProductScout.store'
import WizardPane from './WizardPane'
import ChatPane from './ChatPane'
import './ProductScout.component.scss'

/**
 * Slice-1 fallback disclaimer text. When a conversation is active, the
 * server-snapshotted `disclaimer_text` from `current_conversation` is used
 * instead — this preserves compliance accuracy if disclaimer text changes
 * during a long-running session. Production text lives in
 * `productscout_settings.disclaimer_text` once Slice 2 ships.
 */
const DISCLAIMER_FALLBACK =
  'ProductScout provides informational guidance only. Not a quote. ' +
  'Verify with the carrier brochure before binding.'

/**
 * `ProductScout` — the ProductScout AI assistant body content. Rendered
 * inside the `LiveLeaderboardSidebar`'s tab bar (2026-05-12 architecture
 * change: ProductScout is a tab inside the shared "Live" slider, not its
 * own sidebar). Owns the disclaimer banner and branches between wizard
 * intake and chat based on `ProductScoutStore.is_active`.
 *
 * Wrapped in `observer` (mobx-react-lite) so MobX-driven state changes
 * (conversation start, message arrival, archive) re-render automatically.
 */
function ProductScout() {
  const isActive = ProductScoutStore.is_active
  const isInitializing = ProductScoutStore.is_initializing
  const disclaimerText = ProductScoutStore.current_conversation?.disclaimer_text || DISCLAIMER_FALLBACK

  // Mount-side bootstrap: subscribe eagerly so the WS connection survives
  // page reloads, tab switches, and conversation archives (channel is
  // user-keyed); rehydrate the user's active conversation if one exists on
  // the backend. Both calls are internally silent on failure — wizard is
  // the graceful fallback. `allSettled` runs them in parallel without one
  // throw aborting the other; `finally` guarantees the splash flips off
  // even if a sync throw escapes.
  useEffect(() => {
    if (!ProductScoutStore.is_initializing) return
    ;(async () => {
      try {
        await Promise.allSettled([
          ProductScoutStore.ensureSubscribed(),
          ProductScoutStore.resumeActiveConversation(),
          ProductScoutStore.loadWizardOptions(),
        ])
      } finally {
        runInAction(() => {
          ProductScoutStore.is_initializing = false
        })
      }
    })()
  }, [])

  return (
    <div className='ProductScout'>
      <div className='ProductScout__disclaimer' role='note' aria-label='ProductScout compliance disclaimer'>
        {disclaimerText}
      </div>
      {isInitializing ? <InitializingSplash /> : isActive ? <ChatPane /> : <WizardPane />}
    </div>
  )
}

/**
 * `InitializingSplash` — shown for the brief window between component
 * mount and the resolution of `ensureSubscribed()` + `resumeActiveConversation()`.
 * Replaces the wizard-flash-then-chat transition with a single placeholder
 * so the user reads one state instead of two.
 *
 * Local to this file rather than `src/components/shared/LoadingSpinner` —
 * that shared component is built for full-page overlays on routed pages
 * and is too heavy for this sidebar pane.
 */
function InitializingSplash() {
  return (
    <div className='ProductScout__splash' role='status' aria-live='polite'>
      <span className='ProductScout__splash-spinner' aria-hidden='true' />
      <span className='ProductScout__splash-label'>Loading ProductScout…</span>
    </div>
  )
}

export default observer(ProductScout)
